import { HttpMethod, type IHttpClient } from '@/shared/contracts/http-client.contract';
import type {
  MakeOrderItem,
  MakeOrderPaymentInfo,
  MakeOrderDeliveryInfo,
} from '../types/make-order';

import { ENDPOINT } from '../types/make-order';

import { UIError } from '@/shared/utils/ui-error';
import { redactCardSecrets } from '@/shared/utils/redact-card-secrets';
import { analyticsOrderHeaders } from '@/modules/analytics/store-events/store-events';

export type UseCaseInput = {
  description?: string;
  items: MakeOrderItem[];
  payment_info: MakeOrderPaymentInfo;
  cash_change_amount: number;
  delivery_info: MakeOrderDeliveryInfo;
  schedule_date?: string; // Data do agendamento (ex: "2025-10-25 06:00:00")
  schedule_slot?: string; // Slot de horário (ex: "06:00-07:00")
  store_slug: string;
  // PRAT-70: mesma chave em todo reenvio da mesma tentativa de checkout — a API
  // devolve o pedido já criado em vez de criar outro.
  idempotency_key?: string;
  // PRAT-161: "CPF na nota?" — a API guarda no perfil para os próximos pedidos.
  customer_cpf?: string;
};

type UseCaseOutput = {
  order_id: string;
  unique_order_id: string;
  display_id: number;
  estimated_delivery_time: string;
  total_amount: number;
  delivery_charge: number;
  discount_amount: number;
  final_amount: number;
  // PRAT-161: recompensa ganha ao completar o perfil com o CPF deste pedido.
  profile_rewards?: { label: string }[];
};

export class MakeOrderUsecase {
  private client: IHttpClient;

  constructor(client: IHttpClient) {
    this.client = client;
  }

  async execute(input: UseCaseInput): Promise<UseCaseOutput> {
    const { items = [], payment_info, delivery_info, store_slug } = input;

    if (items.length === 0) {
      throw new UIError('ITEMS_REQUIRED', 'É obrigatório informar pelo menos um item');
    }

    // Carteira pratiGO (PRAT-89): pedido pago só com o saldo vai sem forma de
    // pagamento — o servidor recusa se o saldo não cobrir o total.
    if (
      !payment_info?.use_wallet &&
      (!payment_info?.payment_methods || payment_info.payment_methods.length === 0)
    ) {
      throw new UIError('PAYMENT_METHOD_REQUIRED', 'É obrigatório informar um método de pagamento');
    }

    // Address is only required for delivery orders (type 1), not pickup (type 2)
    if (delivery_info.delivery_type === 1 && !delivery_info.address_id) {
      throw new UIError('ADDRESS_REQUIRED', 'É obrigatório informar um endereço de entrega');
    }

    // PRAT-79: o input pode levar o CVV do cartão; nunca vai cru para o console.
    console.log('INPUT PARA O MAKE ORDER', redactCardSecrets(input));

    const response = await this.client.sendRequest<UseCaseOutput, UseCaseInput>({
      endpoint: ENDPOINT(store_slug),
      method: HttpMethod.POST,
      body: input,
      // PRAT-19: sessão do Desempenho da Loja, para a API registrar o pedido no funil.
      headers: analyticsOrderHeaders(store_slug),
    });

    return {
      order_id: response.order_id,
      unique_order_id: response.unique_order_id,
      display_id: response.display_id,
      estimated_delivery_time: response.estimated_delivery_time,
      total_amount: response.total_amount,
      delivery_charge: response.delivery_charge,
      discount_amount: response.discount_amount,
      final_amount: response.final_amount,
      profile_rewards: response.profile_rewards,
    };
  }
}
